import React from 'react';

import { Typography } from '@mui/material';
import { pendingChangesCounterStyles } from '@sharedCR/common/EntityFormModal/EntityFormModalHead/EntityFormModalHead.styles';
import { ErrorDotIndicator } from '@sharedCR/common/ErrorDotIndicator';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import NavNeededIndicator from '@sharedCR/common/NavNeededIndicator';
import { navNeededRowStyles } from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.styles';
import {
  getAreaIdsByNameAndLevel,
  getNavNeededState,
} from '@sharedCR/common/NavNeededIndicator/NavNeededIndicator.utils';
import PendingChangeWrapper from '@sharedCR/common/PendingChangeWrapper';
import { PresenceEntityDot, PresenceSectionDot } from '@sharedCR/common/PresenceDot';
import RequiredIcon from '@sharedCR/common/RequiredIcon';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import {
  AutoLockFormsLevelType,
  BorrowersSubsectionNames,
  BorrowersTabNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import { AddressIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import _ from 'lodash';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { ExternalContactType, loanSummaryContactsTitles } from 'constants/contacts';
import {
  ADDRESS_COUNTRY_TYPE,
  DemographicInformationProvisionType,
  LanguagePreference,
} from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { COLOR_PALETTE } from 'constants/ui';
import { getConditionalObject } from 'utils/data';
import { calculatePendingChanges, checkIsPendingChangeHasDifference } from 'utils/pendingChanges';

import { openConfirmationDialogAction } from 'store/global/actions';

import {
  Address,
  AlternateName,
  CreditReporting,
  CustomFields,
  Declarations,
  PersonalInfo,
  POA,
} from './Content/MainContent/Borrower';
import { NBECreditReporting, NBECustomFields, NBEPersonalInfo } from './Content/MainContent/NBE';
import {
  BORROWING_ENTITY_TABS_HREF,
  ENTITY_TYPE_HREF,
  NBE_ENTITY_TABS_HREF,
} from './EditEntityModal.hrefs';
import { getTabStyles, navigationTabLabelStyles } from './EditEntityModal.styles';
import { getEntityNavMap } from './EditEntityModal.utils';

// Re-exported from the pure hrefs module so existing consumers reading these names via
// EditEntityModal.constants keep working unchanged; lightweight consumers (loanNavigation) import
// them straight from EditEntityModal.hrefs to avoid pulling in the Borrower component tree.
export { BORROWING_ENTITY_TABS_HREF, ENTITY_TYPE_HREF };

export const BORROWER_TYPE = {
  CO_BORROWER: 'CoBorrowerModel',
  PRIMARY_BORROWER: 'PrimaryBorrowerModel',
};

const BORROWING_ENTITY_CONTENT = {
  [BORROWING_ENTITY_TABS_HREF.BORROWER]: {
    SectionContent: PersonalInfo,
  },
  [BORROWING_ENTITY_TABS_HREF.ADDRESS]: {
    title: 'Current Address',
    icon: <AddressIcon color={COLOR_PALETTE.BLUE[7]} size={16} />,
    SectionContent: Address,
  },
  [BORROWING_ENTITY_TABS_HREF.CREDIT_REPORTING]: {
    SectionContent: CreditReporting,
  },
  [BORROWING_ENTITY_TABS_HREF.ALTERNATE_NAMES]: {
    SectionContent: AlternateName,
  },
  [BORROWING_ENTITY_TABS_HREF.POA]: {
    SectionContent: POA,
  },
  [BORROWING_ENTITY_TABS_HREF.DECLARATIONS]: {
    SectionContent: Declarations,
  },
  [BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS]: {
    SectionContent: CustomFields,
  },
};

const NBE_ENTITY_CONTENT = {
  [NBE_ENTITY_TABS_HREF.NBE_INFO]: {
    SectionContent: NBEPersonalInfo,
  },
  [NBE_ENTITY_TABS_HREF.CREDIT_REPORTING]: {
    SectionContent: NBECreditReporting,
  },
  [NBE_ENTITY_TABS_HREF.CUSTOM_FIELDS]: {
    SectionContent: NBECustomFields,
  },
};

export const NEW_ENTITY = 'new-entity';

export const EDIT_ENTITY_TITLE_CONFIG = {
  [ENTITY_TYPE_HREF.BORROWER]: 'Personal Info',
  [ENTITY_TYPE_HREF.NON_BORROWER]: 'NBE Info',
};

export const NEW_ENTITY_TITLE_CONFIG = {
  [ENTITY_TYPE_HREF.BORROWER]: 'Personal Info',
  [ENTITY_TYPE_HREF.NON_BORROWER]: 'New NBE',
};

export const EDIT_ENTITY_CONTENT_CONFIG = {
  [ENTITY_TYPE_HREF.BORROWER]: BORROWING_ENTITY_CONTENT,
  [ENTITY_TYPE_HREF.NON_BORROWER]: NBE_ENTITY_CONTENT,
};

export const EDIT_ENTITY_TABS_HREF_CONFIG = {
  [ENTITY_TYPE_HREF.BORROWER]: BORROWING_ENTITY_TABS_HREF,
  [ENTITY_TYPE_HREF.NON_BORROWER]: NBE_ENTITY_TABS_HREF,
};

export const EDIT_ENTITY_DEFAULT_SECTION = {
  [ENTITY_TYPE_HREF.BORROWER]: BORROWING_ENTITY_TABS_HREF.BORROWER,
  [ENTITY_TYPE_HREF.NON_BORROWER]: NBE_ENTITY_TABS_HREF.NBE_INFO,
};

export const REMOVE_ENTITY_BUTTON_TEXT = {
  [ENTITY_TYPE_HREF.BORROWER]: loanSummaryContactsTitles[ExternalContactType.BORROWER],
  [ENTITY_TYPE_HREF.NON_BORROWER]: loanSummaryContactsTitles[ExternalContactType.NON_BORROWING],
};

export const BORROWERS_SECTIONS_NAMES = {
  [BORROWING_ENTITY_TABS_HREF.BORROWER]: 'Borrower',
  [BORROWING_ENTITY_TABS_HREF.ADDRESS]: 'Address',
  [BORROWING_ENTITY_TABS_HREF.CREDIT_REPORTING]: 'Credit Reporting',
  [BORROWING_ENTITY_TABS_HREF.ALTERNATE_NAMES]: 'Alternate  Names',
  [BORROWING_ENTITY_TABS_HREF.POA]: 'POA',
  [BORROWING_ENTITY_TABS_HREF.DECLARATIONS]: 'Declarations',
  [BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS]: 'Custom Fields',
};

const BORROWER_ADDRESS_FIELDS = [
  'currentResidence',
  'previousAddresses',
  'mailingAddress',
  'isMailingAddressTheSameAsCurrentAddress',
  'address',
  'subjectProperty',
];

const BORROWER_NON_PERSONAL_INFO_FIELDS = [
  'declarations',
  'creditReporting',
  'powerOfAttorney',
  'alternateNames',
  ...BORROWER_ADDRESS_FIELDS,
];

const BORROWER_CUSTOM_FIELDS_KEY = 'customFields';

const BORROWER_NON_PERSONAL_INFO_PLUS_CUSTOM_FIELDS = [
  ...BORROWER_NON_PERSONAL_INFO_FIELDS,
  BORROWER_CUSTOM_FIELDS_KEY,
];

export const BORROWERS_SECTIONS_EXTRACT_PENDING_CHANGES_ACTION = {
  [BORROWING_ENTITY_TABS_HREF.BORROWER]: d =>
    _.omit(d, BORROWER_NON_PERSONAL_INFO_PLUS_CUSTOM_FIELDS),
  [BORROWING_ENTITY_TABS_HREF.ADDRESS]: d => _.pick(d, BORROWER_ADDRESS_FIELDS),
  [BORROWING_ENTITY_TABS_HREF.CREDIT_REPORTING]: d => _.pick(d, ['creditReporting']),
  [BORROWING_ENTITY_TABS_HREF.ALTERNATE_NAMES]: d => _.pick(d, ['alternateNames']),
  [BORROWING_ENTITY_TABS_HREF.POA]: d => _.pick(d, ['powerOfAttorney']),
  [BORROWING_ENTITY_TABS_HREF.DECLARATIONS]: d => _.pick(d, ['declarations']),
  [BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS]: d => _.pick(d, [BORROWER_CUSTOM_FIELDS_KEY]),
};

export const NEW_BORROWER_REQUIRED_SECTIONS = [
  BORROWING_ENTITY_TABS_HREF.BORROWER,
  BORROWING_ENTITY_TABS_HREF.ADDRESS,
  BORROWING_ENTITY_TABS_HREF.DECLARATIONS,
];

const BORROWERS_NAVIGATION_TABS = Object.entries(BORROWERS_SECTIONS_NAMES).map(
  ([value, label]) => ({
    label,
    value,
    isRequired: NEW_BORROWER_REQUIRED_SECTIONS.includes(value),
    extractPendingChangesAction: BORROWERS_SECTIONS_EXTRACT_PENDING_CHANGES_ACTION[value],
  }),
);

const NBE_CUSTOM_FIELDS_KEY = 'customFields';

const NBE_ENTITY_NAVIGATION_TABS = [
  {
    label: 'NBE Info',
    value: NBE_ENTITY_TABS_HREF.NBE_INFO,
    extractPendingChangesAction: pendingChanges => _.omit(pendingChanges, NBE_CUSTOM_FIELDS_KEY),
  },
  {
    label: 'Credit Reporting',
    value: NBE_ENTITY_TABS_HREF.CREDIT_REPORTING,
    extractPendingChangesAction: _.noop,
  },
  {
    label: 'Custom Fields',
    value: NBE_ENTITY_TABS_HREF.CUSTOM_FIELDS,
    extractPendingChangesAction: d => _.pick(d, [NBE_CUSTOM_FIELDS_KEY]),
  },
];

const ENTITY_NAVIGATION_TABS_CONFIG = {
  [ENTITY_TYPE_HREF.BORROWER]: BORROWERS_NAVIGATION_TABS,
  [ENTITY_TYPE_HREF.NON_BORROWER]: NBE_ENTITY_NAVIGATION_TABS,
};

const newNBEInitialData = [
  {
    fullName: 'New',
    id: NEW_ENTITY,
    address: {
      country: ADDRESS_COUNTRY_TYPE.USA,
    },
  },
];

const newBorrowerInitialData = [
  {
    fullName: 'New Borrower',
    id: NEW_ENTITY,
    isLiveTogetherForTwoYears: false,
    isPrimaryBorrower: false,
    currentResidence: {
      country: ADDRESS_COUNTRY_TYPE.USA,
    },
    mailingAddress: {
      country: ADDRESS_COUNTRY_TYPE.USA,
    },
    isMailingAddressTheSameAsCurrentAddress: true,
    declarations: {
      militaryService: {
        isServedInMilitary: false,
        isExemptVAFundingFee: false,
      },
      languagePreference: {
        languagePreference: LanguagePreference.ENGLISH,
      },
      propertyAndLoan: {
        partOfDownPaymentBorrowed: false,
        loanOnAnotherProperty: false,
        anyNewCreditOutsideOfTheLoan: false,
        isPropertyLien: false,
        relationshipWithPropertySeller: false,
      },
      finances: {
        guarantorOnAnyDebtOutsideTheLoan: false,
        outstandingJudgements: false,
        presentlyObligatedOnAnyLoanOrDebt: false,
        partyToLawSuite: false,
        propertyInLieuOfForeclosureInPastSevenYears: false,
        foreclosureSoldLessOutstandingBalanceWithinPastSevenYears: false,
      },
      demographicInformation: {
        demographicInformationProvisionType: DemographicInformationProvisionType.EMAIL_OR_INTERNET,
      },
    },
  },
];

export const NEW_ENTITY_INITIAL_DATA = {
  [ENTITY_TYPE_HREF.BORROWER]: newBorrowerInitialData,
  [ENTITY_TYPE_HREF.NON_BORROWER]: newNBEInitialData,
};

// Per-borrower completeness roll-up (DEV-4810). Resolves a drawer nav item to the appFormsTree
// area id(s) its "Needed" count folds over, then reads the count from the borrower-scoped roll-up
// map (`selectIncompleteLinkedNavByBorrower` → `{ [areaId]: { needed, count } }` for ONE borrower)
// merged with the loan-scoped map, whose items belong to no single borrower.

// Each left-nav section → its appFormsTree TAB name. All but personal-info camelCase-match their
// tab name; personal-info's appFormsTree tab is `borrower`, so it needs the explicit entry (custom
// fields has no completeness area → resolves to [] → no indicator).
const BORROWER_SECTION_APP_FORMS_TAB = {
  [BORROWING_ENTITY_TABS_HREF.BORROWER]: BorrowersTabNames.BORROWER,
  [BORROWING_ENTITY_TABS_HREF.ADDRESS]: BorrowersTabNames.ADDRESS,
  [BORROWING_ENTITY_TABS_HREF.CREDIT_REPORTING]: BorrowersTabNames.CREDIT_REPORTING,
  [BORROWING_ENTITY_TABS_HREF.ALTERNATE_NAMES]: BorrowersTabNames.ALTERNATE_NAMES,
  [BORROWING_ENTITY_TABS_HREF.POA]: BorrowersTabNames.POA,
  [BORROWING_ENTITY_TABS_HREF.DECLARATIONS]: BorrowersTabNames.DECLARATIONS,
};

// A borrower's WHOLE-drawer total folds over the `borrowers` subsection area (the same area the
// borrowers-list badge uses, but scoped to this one borrower via `borrowerNavMap`).
const getBorrowerNeededTotal = borrowerNavMap =>
  getNavNeededState(
    borrowerNavMap,
    getAreaIdsByNameAndLevel(BorrowersSubsectionNames.BORROWERS, AutoLockFormsLevelType.SUBSECTION),
  ).count;

// A single section's `{ isNeeded, count }` for a borrower — resolves the section to its TAB area id(s).
const getBorrowerSectionNeeded = (borrowerNavMap, sectionValue) =>
  getNavNeededState(
    borrowerNavMap,
    getAreaIdsByNameAndLevel(
      BORROWER_SECTION_APP_FORMS_TAB[sectionValue],
      AutoLockFormsLevelType.TAB,
    ),
  );

export const getHeadTabs = ({
  applicationId,
  tab,
  subtab,
  section,
  data,
  history,
  deletedEntities,
  newEntities,
  pendingChangesCounts,
  pendingChanges,
  isNestedEntityEditActive,
  dispatch,
  setIsNestedEntityEditActive,
  invalidHeadTabs = [],
  navNeededByBorrower = {},
  navNeededLoanScoped = {},
}) =>
  data.map(entity => {
    const entityId = entity.value ?? entity.id;
    const neededCount = getBorrowerNeededTotal(
      getEntityNavMap({ subtab, entityId, navNeededByBorrower, navNeededLoanScoped }),
    );
    const isDeleted = deletedEntities.includes(entity.id);
    const isNewEntity = newEntities.includes(entity.id);
    const { isModified } = ['firstName', 'middleName', 'lastName', 'suffix'].reduce(
      (acc, pendingChangeKey) => {
        acc.isModified =
          !isNewEntity &&
          (acc.isModified ||
            checkIsPendingChangeHasDifference(pendingChanges[entity.id]?.[pendingChangeKey]));

        return acc;
      },
      { isModified: false },
    );

    return {
      value: entityId,
      label: (
        <>
          <PresenceEntityDot entityId={entityId} />
          {entity.label ?? entity.fullName}
          {!!neededCount && <NavNeededCountBadge count={neededCount} />}
        </>
      ),
      isModified,
      isInvalid: invalidHeadTabs.includes(entityId),
      customStyles: getTabStyles(isDeleted),
      onClick: () => {
        const navigate = () =>
          history.push(
            LoansPaths.applicationManagement.generatePath({
              applicationId,
              entityId: entity.value ?? entity.id,
              tab,
              subtab,
              section,
            }),
          );

        if (isNestedEntityEditActive) {
          dispatch(
            openConfirmationDialogAction({
              type: ConfirmationModalType.WARNING,
              message: 'You are leaving this tab. Unsaved changes will be lost.',
              onAccept: () => {
                navigate();
                setIsNestedEntityEditActive(false);
              },
              onAcceptButtonName: 'Proceed',
              withButtons: true,
            }),
          );

          return;
        }

        navigate();
      },
      pendingChangesCount: [...newEntities, ...deletedEntities].includes(entity.id)
        ? 1
        : pendingChangesCounts[entity.id],
    };
  });

export const getNavigationTabs = ({
  applicationId,
  entityId,
  tab: pageTab,
  subtab,
  section,
  history,
  deletedEntities,
  pendingChanges,
  newEntities,
  isNestedEntityEditActive,
  setIsNestedEntityEditActive,
  dispatch,
  invalidTabs = [],
  hiddenPageIds,
  hiddenFieldIds,
  shouldShowBorrowerCustomFields = false,
  shouldShowNbeCustomFields = false,
  navNeededByBorrower = {},
  navNeededLoanScoped = {},
}) => {
  const tabs = ENTITY_NAVIGATION_TABS_CONFIG[subtab];
  const isDeleted = deletedEntities.includes(entityId);
  const isNewEntity = newEntities.includes(entityId);
  const isCustomFieldsSectionActive = section === BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS;
  const borrowerNavMap = getEntityNavMap({
    subtab,
    entityId,
    navNeededByBorrower,
    navNeededLoanScoped,
  });

  return tabs?.reduce((acc, tab) => {
    if (checkIsTabHidden(tab.value, hiddenPageIds, hiddenFieldIds)) {
      return acc;
    }

    if (
      subtab === ENTITY_TYPE_HREF.BORROWER &&
      tab.value === BORROWING_ENTITY_TABS_HREF.CUSTOM_FIELDS &&
      !shouldShowBorrowerCustomFields &&
      !isCustomFieldsSectionActive
    ) {
      return acc;
    }

    if (
      subtab === ENTITY_TYPE_HREF.NON_BORROWER &&
      tab.value === NBE_ENTITY_TABS_HREF.CUSTOM_FIELDS &&
      !shouldShowNbeCustomFields &&
      !isCustomFieldsSectionActive
    ) {
      return acc;
    }

    const sectionPendingChanges =
      tab.extractPendingChangesAction(pendingChanges[entityId] ?? {}) ?? {};

    const { count: pendingChangesCount } = calculatePendingChanges(sectionPendingChanges);
    const { isNeeded: isSectionNeeded } = getBorrowerSectionNeeded(borrowerNavMap, tab.value);
    const tabName =
      subtab === ENTITY_TYPE_HREF.NON_BORROWER &&
      entityId === NEW_ENTITY &&
      tab.value === NBE_ENTITY_TABS_HREF.NBE_INFO
        ? 'New NBE'
        : tab.label;

    acc.push({
      ...tab,
      label: (
        <Typography sx={navigationTabLabelStyles} variant="regular12">
          {!!pendingChangesCount && !isNewEntity && !isDeleted && (
            <PendingChangeWrapper
              isSmall
              className="pending-changes-count-wrapper"
              pendingChangesActionType={PendingChangesActionType.MODIFIED}
              sx={pendingChangesCounterStyles}
              textSelector="span"
            >
              <Typography className="pending-changes-count" variant="regular12">
                {pendingChangesCount}
              </Typography>
            </PendingChangeWrapper>
          )}
          <PresenceSectionDot sectionId={tab.value} />
          {tabName} {tab.isRequired && isNewEntity && <RequiredIcon />}
          {invalidTabs.includes(tab.value) && <ErrorDotIndicator />}
          {isSectionNeeded && <NavNeededIndicator />}
        </Typography>
      ),
      // Plain-text name + needed flag for the collapsed section dropdown on
      // tablet/mobile — its options render `displayName`, not the rich JSX label,
      // so without these the dropdown falls back to the raw slug and drops the
      // "Needed" signal (EntityFormModalMain renders the indicator from isNeeded).
      displayName: tabName,
      isNeeded: isSectionNeeded,
      isDeleted,
      customStyles: getConditionalObject(isSectionNeeded, navNeededRowStyles),
      onClick: () => {
        const navigate = () =>
          history.push(
            LoansPaths.applicationManagement.generatePath({
              applicationId,
              entityId,
              tab: pageTab,
              subtab,
              section: tab.value,
            }),
          );

        if (isNestedEntityEditActive) {
          dispatch(
            openConfirmationDialogAction({
              type: ConfirmationModalType.WARNING,
              message: 'You are leaving this tab. Unsaved changes will be lost.',
              onAccept: () => {
                navigate();
                setIsNestedEntityEditActive(false);
              },
              onAcceptButtonName: 'Proceed',
              withButtons: true,
            }),
          );

          return;
        }

        navigate();
      },
    });

    return acc;
  }, []);
};
